import type { BookingStatus, PortalDeclined, PortalDog, PortalInfo, PortalStay, StayPhase } from '@bbbk/schema';
import { stayPhase } from '@bbbk/schema';
import { parseVaccineDue } from '../components/bookings/domain';
import { MONTHS_ABBR } from '../lib/dates';
import { dogNames, longDay, money, shortDay } from './requestRules';

// THE RULES BEHIND HOME, THE STAY PAGE, YOUR DOGS AND INFO — pure, so every one
// of them is a unit test and the components only draw (customer-home-stays).
// The phase itself is `stayPhase` in @bbbk/schema: status leads, dates only
// split a phase, and every screen here reads that one function (AC3).

/* ----- the card: in date only when both dates are (AC7) ----- */

export type CardState = 'ok' | 'due' | 'lapsed' | 'none';

const monthIdx = (iso: string) => Number(iso.slice(0, 4)) * 12 + Number(iso.slice(5, 7));

/**
 * A dog carries two dates — the annual jab and kennel cough — and the card is
 * only as good as the earlier one. Expired reads from today; expiring before
 * the stay reads from the next check-in; the desk's own rule, month-granular
 * (`vaccineState`), read the annual date alone (#244 review).
 */
export function cardOf(
  dog: { vaccinesDue?: string | null; kcDue?: string | null },
  today: string,
  nextCheckIn: string | null,
): { state: CardState; until: string | null } {
  // BOTH DATES, OR THE CARD IS WANTED (#245 review): the kennel needs the
  // annual and the kennel-cough date, and one on file with one missing was
  // reading as in date. The registration's own tag rule holds here: an expiry
  // outranks a missing date (P2.30), a missing date outranks everything else.
  const parsed = [dog.vaccinesDue, dog.kcDue].map(parseVaccineDue);
  const present = parsed.filter((d): d is { year: number; month: number } => d !== null);
  if (present.length === 0) return { state: 'none', until: null };
  const d = present.reduce((a, b) => (a.year * 12 + a.month <= b.year * 12 + b.month ? a : b));
  const idx = d.year * 12 + d.month;
  const until = `${MONTHS_ABBR[d.month - 1]} ${d.year}`;
  if (idx < monthIdx(today)) return { state: 'lapsed', until };
  if (present.length < parsed.length) return { state: 'none', until };
  if (nextCheckIn && idx < monthIdx(nextCheckIn)) return { state: 'due', until };
  return { state: 'ok', until };
}

/* ----- the notices: ranked by what they cost, two the ceiling (AC8) ----- */

export type DoorKind = 'deposit' | 'balance' | 'upload' | 'view';
/** The plain door: a card with nothing owed opens Patricia's thread, nothing typed. */
export type MessageKind = 'message';

export interface Notice {
  tone: 'alert' | 'gold' | 'ok';
  ico: 'warn' | 'syringe' | 'euro';
  title: string;
  detail: string;
  door: DoorKind;
  /** The stay it is about; null for a card asked for with no stay coming (Freddi, 2026-10-13). */
  stay: PortalStay | null;
  /** The one dog the notice is about, when it is about one. */
  dog?: string;
  /** That dog's id — the dogs drawer opens on its row (vaccination-cards AC1). */
  dogId?: string;
}

/* ----- the card's answer: what a dog's card asks of Home (vaccination-cards) ----- */

export type CardNeed = 'paused' | 'refused' | 'ask' | 'checking' | 'verified';

/** An accept is news for this long (AC3), then the card simply is in date. */
const VERIFIED_DAYS = 7;
const daysBetween = (fromIso: string, toDay: string) => Math.floor((Date.parse(toDay + 'T12:00:00Z') - Date.parse(fromIso)) / 86_400_000);

/**
 * PATRICIA'S ANSWER FIRST, THEN THE DATES. A card with her, refused, or over
 * to her (AC2, AC9, AC10) says so whatever the dog's dates read — the dates
 * are the claim she has not answered. Otherwise the dates decide (`cardOf`):
 * expired, expiring before the stay, or missing asks for a card (AC11); an
 * accept is news for seven days (AC3); anything else asks nothing.
 */
export function cardNeed(dog: PortalDog, today: string, nextCheckIn: string | null): CardNeed | null {
  const review = dog.card.review ?? null;
  if (review?.state === 'paused') return 'paused';
  if (review?.state === 'not_accepted') return 'refused';
  if (review?.state === 'checking') return 'checking';
  const c = cardOf(dog, today, nextCheckIn);
  // MARKED VALID IS VALID (Freddi, 2026-10-13: "if we have marked vaccinations valid the customer
  // should not see any vaccinations due message"): her accept outranks a date the card did not carry.
  // Not a date that has passed, and not one that runs out before the stay — the desk reads that
  // *Runs out before the stay*, and the customer is the one who can fix it (PR #260 review).
  const accepted = review?.state === 'accepted';
  if (c.state === 'lapsed' || c.state === 'due' || (c.state !== 'ok' && !accepted)) return 'ask';
  if (accepted && daysBetween(review.at, today) <= VERIFIED_DAYS) return 'verified';
  return null;
}

/** The order a card's notice is chosen in: the stuck first, the customer's move next, news last. */
const NEED_ORDER: CardNeed[] = ['paused', 'refused', 'ask', 'checking', 'verified'];

const NOTICE_CEILING = 2;

/**
 * The stay a card is measured against: the first still to come — asked for or booked, not begun.
 * A stay the dogs are in now has been judged already; the next one is what the card must cover
 * (PR #260 review). `stays` in Home's order (`homeStays`).
 */
export function upcomingStay(stays: readonly PortalStay[], today: string): PortalStay | null {
  return stays.find((s) => { const p = stayPhase(s, today); return p === 'requested' || p === 'before'; }) ?? null;
}

/**
 * `stays` is what Home draws, next first (`homeStays`). The deposit that loses
 * the dates comes first, then the cards that stop admission; a balance is
 * never a notice before collection, and is the one notice after it while it
 * is owed. Collection day carries none — the balance is the page.
 */
export function notices(stays: readonly PortalStay[], dogs: readonly PortalDog[], today: string): Notice[] {
  const lead = stays[0];
  const phase = lead ? stayPhase(lead, today) : null;
  const out: Notice[] = [];
  // NEWS LAST (PR #260 review): a card with Patricia, or one she accepted, asks nothing of the
  // customer, so it never takes a capped slot from money still owed.
  const news: Notice[] = [];
  // A SCREENSHOT WAITING ON PATRICIA (deposit-proof AC7, AC18; Freddi,
  // 2026-10-11: "once the screenshot is sent lets remove the needs you info
  // message"): nothing under Needs you — the stay card alone says the
  // screenshot is with her, over its door drawn spent (`cardDoor`). Nothing
  // reopens the screenshot; the customer never sees it again.
  // Either kind (PR #255 review, #23): a balance screenshot waiting is not a deposit to chase.
  if (!lead) {
    // no stay, no money notice
  } else if (lead.proofPending) {
    // no notice
  } else if (lead.proofRefused === 'remainder' && lead.due > 0 && phase !== 'requested') {
    // A NO ON THE BALANCE IS A NOTICE IN ANY PHASE (Freddi, 2026-10-11: "that user
    // should have a needs you info message they can upload the screenshot to"):
    // Patricia has asked again, so it is the customer's move, before, during or after.
    out.push({ tone: 'alert', ico: 'warn', title: 'Patricia couldn’t match your screenshot', detail: `Transfer the ${money(lead.due)} balance`, door: 'balance', stay: lead });
  } else if (phase === 'before' && lead.status === 'unconfirmed' && lead.due > 0 && lead.proofRefused === 'deposit') {
    // HER NO (AC11; ruled 2026-10-11): the screenshot could not be matched,
    // the deposit is asked for again, and the Transfer door is back.
    out.push({ tone: 'alert', ico: 'warn', title: 'Patricia couldn’t match your screenshot', detail: `Transfer the ${money(depositLeft(lead))} deposit`, door: 'deposit', stay: lead });
  } else if (phase === 'before' && lead.status === 'unconfirmed' && lead.due > 0) {
    // Part of the deposit in: the notice says what is still to come (#245 review).
    const left = depositLeft(lead);
    out.push({ tone: 'alert', ico: 'warn', title: 'Deposit not received', detail: left < lead.deposit ? `${money(left)} of the ${money(lead.deposit)} deposit still to come` : `${money(lead.deposit)} required to secure booking`, door: 'deposit', stay: lead });
  }
  {
    // ONE CARD NOTICE, THE MOST PRESSING (vaccination-cards): the dogs grouped by what their card
    // asks, and the first group in NEED_ORDER is the notice. A stay coming up judges its own dogs
    // against its arrival; otherwise every dog is judged against today — expired or missing asks,
    // stay or no stay (Freddi, 2026-10-13: "for all customers with expired vaccination dates/no
    // vaccination dates entered we want to show the needs you message").
    const ahead = upcomingStay(stays, today);
    const needs = dogs
      .filter((d) => !ahead || ahead.dogIds.includes(d.id))
      .map((d) => ({ d, need: cardNeed(d, today, ahead?.checkIn ?? null) }));
    const kind = NEED_ORDER.find((k) => needs.some((x) => x.need === k)) ?? null;
    const group = needs.filter((x) => x.need === kind).map((x) => x.d);
    const only = group.length === 1 ? group[0]! : null;
    // Several dogs, one notice: the words are the household's, and the door still opens on the first
    // of them, never on a dog with nothing wrong (PR #260 review).
    const named = only ? { dog: only.name, dogId: only.id } : group[0] ? { dogId: group[0].id } : {};
    if (kind === 'paused') {
      // No count, no retry (AC9; decision 231): the card is hers to sort, in the thread.
      out.push({ tone: 'alert', ico: 'syringe', title: only ? `${only.name}’s card needs Patricia` : 'Your dogs’ cards need Patricia', detail: 'Message her and she’ll sort it', door: 'view', stay: ahead, ...named });
    } else if (kind === 'refused') {
      out.push({ tone: 'alert', ico: 'syringe', title: only ? `${only.name}’s card didn’t pass` : 'Your dogs’ cards didn’t pass', detail: 'Upload the card again', door: 'upload', stay: ahead, ...named });
    } else if (kind === 'checking') {
      news.push({ tone: 'gold', ico: 'syringe', title: only ? `Patricia is confirming ${only.name}’s card` : 'Patricia is confirming your dogs’ cards', detail: 'She usually replies the same day', door: 'view', stay: ahead, ...named });
    } else if (kind === 'verified') {
      const until = only ? cardOf(only, today, ahead?.checkIn ?? null).until : null;
      news.push({ tone: 'ok', ico: 'syringe', title: only ? `${only.name}’s vaccines are up to date` : 'Your dogs’ vaccines are up to date', detail: until ? `Valid until ${until}` : 'Nothing else needed before the stay', door: 'view', stay: ahead, ...named });
    }
    const cards = kind === 'ask' ? group.map((d) => ({ d, c: cardOf(d, today, ahead?.checkIn ?? null) })) : [];
    if (cards.length > 0) {
      // One state, one verb; a mix needs attention; no card at all is missing (#245 review).
      const states = new Set(cards.map((x) => x.c.state));
      const verb = states.size > 1 ? 'need attention' : states.has('lapsed') ? 'have expired' : states.has('due') ? 'expire soon' : null;
      const one = cards.length === 1 ? cards[0]!.d.name : null;
      out.push({
        tone: 'gold', ico: 'syringe',
        title: verb ? (one ? `${one}’s vaccines ${verb}` : `Your dogs’ vaccines ${verb}`) : one ? `${one}’s vaccination card is missing` : 'Your dogs’ vaccination cards are missing',
        detail: ahead ? `Due before arrival on ${longDay(ahead.checkIn)}` : one ? `Upload ${one}’s card` : 'Upload their cards',
        door: 'upload', stay: ahead, ...(one ? { dog: one, dogId: cards[0]!.d.id } : { dogId: cards[0]!.d.id }),
      });
    }
  }
  for (const s of stays) {
    // A balance whose screenshot is with her carries no notice either (ruled 2026-10-11).
    if (s.proofPending) continue;
    if (s === lead && s.proofRefused === 'remainder') continue; // already said above
    if (stayPhase(s, today) === 'after' && s.due > 0) {
      out.push({ tone: 'alert', ico: 'euro', title: `${money(s.due)} still to pay`, detail: `From the stay on ${longDay(s.checkIn)}`, door: 'balance', stay: s });
    }
  }
  return [...out, ...news].slice(0, NOTICE_CEILING);
}

/**
 * A DECLINE, IN NEUTRAL WORDS (accept-or-decline AC11; requirements ruling 4).
 * Decline is her judgement and is offered off-peak too, and the app records
 * no reason (231), so the notice names only the dogs and the dates — never
 * *is full*, never a reason, and no refund line because nothing was asked.
 */
export function declinedTitle(d: PortalDeclined): string {
  return `Patricia couldn’t take ${dogNames(d.dogs)} for ${rangeLabel(d.checkIn, d.checkOut)}`;
}

/* ----- the doors: what they say to Patricia (AC5a) ----- */

/** `Sat 17 – Tue 20 Oct`, the repeated month dropped. */
export function rangeLabel(checkIn: string, checkOut: string): string {
  const sameMonth = checkIn.slice(0, 7) === checkOut.slice(0, 7);
  return `${sameMonth ? shortDay(checkIn) : longDay(checkIn)} – ${longDay(checkOut)}`;
}

export interface OpenerContext {
  contactName: string;
  dogs: readonly string[];
  stay?: PortalStay;
  dog?: string;
}

/**
 * Until a door's own row ships, it opens Message Patricia with a line naming
 * what it has: the thing, the dogs, the dates and the reference.
 *
 * THE LINE STARTS AFTER THE GREETING. `whatsappHref` types "Hi Patricia, "
 * itself, as it does for every other door in the portal; an opener that
 * greets her too reads "Hi Patricia, Hi Patricia, …" in the customer's
 * message box — which is what every door on this branch did until the
 * request's door was pinned (2026-10-09).
 */
export function opener(kind: DoorKind, ctx: OpenerContext): string {
  const who = dogNames(ctx.dogs);
  const when = ctx.stay ? `${rangeLabel(ctx.stay.checkIn, ctx.stay.checkOut)}${ctx.stay.reference ? ` (${ctx.stay.reference})` : ''}` : '';
  if (kind === 'deposit') return `I’d like to pay the ${money(ctx.stay ? depositLeft(ctx.stay) : 0)} deposit for ${who}, ${when}.`;
  if (kind === 'balance') return `I’d like to pay the ${money(ctx.stay?.due ?? 0)} balance for ${who}, ${when}.`;
  // A card's door, Upload or View — reached only where no drawer can open it.
  const card = ctx.dog ? `${ctx.dog}’s vaccination card` : 'the dogs’ vaccination cards';
  return ctx.stay ? `I’d like to send ${card} ahead of ${when}.` : `I’d like to send ${card}.`;
}

/* ----- the stay card's words and rows ----- */

/** `09:00-10:00` → `9–10am`, as the prototype prints a handover window. */
export function windowLabel(w: string): string {
  const [a, b] = w.split('-');
  if (!a || !b) return w;
  const part = (t: string) => {
    const [h = 0, m = 0] = t.split(':').map(Number);
    const hh = ((h + 11) % 12) + 1;
    return { txt: m ? `${hh}:${String(m).padStart(2, '0')}` : `${hh}`, sfx: h >= 12 ? 'pm' : 'am' };
  };
  const A = part(a);
  const B = part(b);
  return A.sfx === B.sfx ? `${A.txt}–${B.txt}${B.sfx}` : `${A.txt}${A.sfx}–${B.txt}${B.sfx}`;
}

export type StageState = 'done' | 'now' | 'todo';

/** Requested · Accepted · Deposit paid · Booked — which is lit, from the status. */
export function stageStrip(status: BookingStatus): [StageState, StageState, StageState, StageState] {
  if (status === 'requested') return ['now', 'todo', 'todo', 'todo'];
  if (status === 'unconfirmed') return ['done', 'done', 'now', 'todo'];
  return ['done', 'done', 'done', 'done'];
}

export interface MoneyRow {
  label: string;
  value: string;
  /** The tag beside the label — Paid, Due, or the request's quiet *if accepted*. */
  tag?: string;
  cls?: string;
}

/**
 * *Stay · Deposit · Due* (R-HOME-2). A request owes nothing: Stay and the
 * deposit if accepted, no Due row. Settled reads *Paid in full*. A zero-total
 * booking shows no money at all — neither owed nor paid, no money moved (AC11).
 */
export function moneyRows(stay: PortalStay, phase: StayPhase): MoneyRow[] {
  if (stay.total <= 0) return [];
  const rows: MoneyRow[] = [{ label: 'Stay', value: money(stay.total) }];
  if (phase === 'requested') return [...rows, { label: 'Deposit', value: money(stay.deposit), tag: 'if accepted' }];
  // An accepted stay's deposit is the quote; what is still to come of it is
  // Due, and what has come in so far is its own row (#245 review: €40 of a
  // €100 deposit read as the whole deposit Paid).
  const left = stay.status === 'unconfirmed' ? depositLeft(stay) : 0;
  const received = Math.max(0, stay.total - stay.due);
  if (stay.deposit > 0 || left > 0) rows.push({ label: 'Deposit', value: money(stay.deposit), tag: left > 0 ? 'Due' : 'Paid' });
  if (left > 0 && received > 0) rows.push({ label: 'Received', value: money(received), tag: 'Paid' });
  if (stay.due > 0) return [...rows, { label: 'Due', value: money(stay.due), cls: 'tot' }];
  const balance = stay.total - stay.deposit;
  if (balance > 0) rows.push({ label: 'Balance', value: money(balance), tag: 'Paid' });
  return [...rows, { label: 'Paid in full', value: money(stay.total), cls: 'tot paid' }];
}

// A stay with no time on file keeps the default of its end — the morning for a
// drop-off, the afternoon for a collection (#245 review: it read the afternoon twice).
const partWord = (half: string | undefined, info: PortalInfo | null, bare: 'morning' | 'afternoon') =>
  info ? windowLabel((half ?? (bare === 'morning' ? 'am' : 'pm')) === 'am' ? info.handoverAm : info.handoverPm) : `in the ${bare}`;

/** What is still to come of an accepted stay's deposit: the quote less what has been received. */
export const depositLeft = (stay: PortalStay) => Math.max(0, stay.deposit - Math.max(0, stay.total - stay.due));

/** `1 night`, `3 nights` (#245 review: "1 nights"). */
export const nightsLabel = (checkIn: string, checkOut: string) => {
  const n = nightsOf(checkIn, checkOut);
  return `${n} night${n === 1 ? '' : 's'}`;
};

/** The one sentence on the card, in the customer's words: the state in bold, then what follows from it. */
export function cardSay(stay: PortalStay, phase: StayPhase, info: PortalInfo | null): { ico: 'clock' | 'euro' | 'tick'; lead: string; rest: string } {
  const dropWindow = partWord(stay.dropOff, info, stay.dropOff === 'pm' ? 'afternoon' : 'morning');
  const collectWindow = partWord(stay.collection, info, stay.collection === 'am' ? 'morning' : 'afternoon');
  if (phase === 'requested') return { ico: 'clock', lead: 'Nothing to do yet.', rest: 'Patricia confirms your request, usually the same day.' };
  // A screenshot with her, or one she could not match (deposit-proof AC7, AC11, AC18).
  if (stay.proofPending) return { ico: 'clock', lead: 'Your screenshot is with Patricia.', rest: 'She usually replies the same day.' };
  if (phase === 'before' && stay.status === 'unconfirmed' && stay.proofRefused === 'deposit') {
    return { ico: 'euro', lead: 'Patricia couldn’t match your screenshot.', rest: `Send the transfer screenshot again — ${money(depositLeft(stay))} to secure your booking.` };
  }
  if (stay.proofRefused === 'remainder' && stay.due > 0) {
    return { ico: 'euro', lead: 'Patricia couldn’t match your screenshot.', rest: `Send the transfer screenshot again — ${money(stay.due)} still to pay.` };
  }
  if (phase === 'before' && stay.status === 'unconfirmed') {
    // The part of the deposit still to come, as the door and the notice say it (#245 review).
    const left = depositLeft(stay);
    return { ico: 'euro', lead: 'Patricia has accepted your dates.', rest: left < stay.deposit ? `${money(left)} more to secure your booking.` : `${money(stay.deposit)} due to secure your booking.` };
  }
  if (phase === 'before') return { ico: 'tick', lead: 'You’re all booked.', rest: `See you ${longDay(stay.checkIn)} · drop off ${dropWindow}` };
  if (phase === 'staying') return { ico: 'tick', lead: 'The dogs are in.', rest: `Collect ${longDay(stay.checkOut)}, ${collectWindow}` };
  if (phase === 'collection') return { ico: 'tick', lead: stay.collection === 'am' ? 'See you this morning.' : 'See you this afternoon.', rest: `Collection ${collectWindow}` };
  return { ico: 'tick', lead: 'Thanks for coming.', rest: `The ${money(stay.due)} from this stay is still to settle.` };
}

export interface CardDoor { kind: DoorKind | MessageKind; label: string; primary: boolean; /** Drawn spent: this line's screenshot is with Patricia (ruled 2026-10-11). */ disabled?: boolean }

/**
 * EVERY CARD ENDS IN EXACTLY ONE DOOR (Freddi, 2026-10-09; deviations 10 and
 * 13). Money first: while anything is owed the door is the money's — the
 * deposit loud, the balance quiet before arrival and loud from the day they
 * are in. When nothing is owed — a request, a stay paid in full before,
 * during or on the day — the door is the standard Message Patricia, which
 * opens her thread and types nothing. Nothing is drawn disabled (AC5) but a
 * money door whose screenshot is with Patricia (ruled 2026-10-11);
 * a settled stay is a row and has no foot.
 */
export function cardDoor(stay: PortalStay, phase: StayPhase): CardDoor | null {
  if (stay.due > 0 && phase !== 'requested') {
    // THE ONE EXCEPTION TO "NOTHING DRAWN DISABLED" (customer-home-stays AC5):
    // while this line's screenshot is with Patricia the money door stays, spent
    // (deposit-proof AC18; Freddi, 2026-10-11) — the sentence above it says why.
    const spent = stay.proofPending ? { disabled: true as const } : {};
    // THE LABEL IS THE ACT (Freddi, 2026-10-11: "something short, articulate,
    // succinct"): the sheet is where they transfer and prove it; the amount is
    // in the card's sentence above, so the door says only what to do.
    // The part of the deposit still to come — never a zero deposit (#245 review).
    const left = depositLeft(stay);
    if (phase === 'before' && stay.status === 'unconfirmed' && left > 0) return { kind: 'deposit', label: 'Upload deposit proof', primary: true, ...spent };
    if (phase === 'before') return { kind: 'balance', label: 'Upload balance proof', primary: false, ...spent };
    return { kind: 'balance', label: 'Upload balance proof', primary: true, ...spent };
  }
  if (phase === 'after' || phase === 'hidden') return null;
  return { kind: 'message', label: 'Message Patricia', primary: false };
}

/** The dogs on a stay, by id, with the joined names as the fallback for a stay that predates `dogIds`. */
export function dogsOn(stay: PortalStay, dogs: readonly PortalDog[]): { names: string[]; dogs: PortalDog[] } {
  const on = dogs.filter((d) => stay.dogIds.includes(d.id));
  if (on.length > 0) return { names: on.map((d) => d.name), dogs: on };
  // A stay that predates `dogIds` keeps its typed names; one with none is still named (#245 review).
  const typed = stay.dogs ? stay.dogs.split(',').map((s) => s.trim()).filter(Boolean) : [];
  return { names: typed.length ? typed : ['your dogs'], dogs: [] };
}

export const nightsOf = (checkIn: string, checkOut: string) => Math.max(0, Math.round((Date.UTC(+checkOut.slice(0, 4), +checkOut.slice(5, 7) - 1, +checkOut.slice(8, 10)) - Date.UTC(+checkIn.slice(0, 4), +checkIn.slice(5, 7) - 1, +checkIn.slice(8, 10))) / 86_400_000));
